import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  atomWithReset,
  atomWithStorage,
  createJSONStorage,
  useResetAtom,
} from 'jotai/utils';
import { CJEvent } from 'types/event';

const collapsedDaysAtom = atomWithReset<string[]>([]);

export const useResetCollapsedDays = () => useResetAtom(collapsedDaysAtom);
export const useCollapsedDays = () => useAtomValue(collapsedDaysAtom);

const toggleCollapsedDayAtom = atom(null, (get, set, dayString: string) => {
  const prev = get(collapsedDaysAtom);
  const updated = prev.includes(dayString)
    ? prev.filter(elt => elt !== dayString)
    : [...prev, dayString];
  return set(collapsedDaysAtom, updated);
});

const toggleMultipleCollapsedDayAtom = atom(
  null,
  (get, set, dayStrings: string[]) => {
    const prev = get(collapsedDaysAtom);
    const updated = [...prev];

    dayStrings.forEach(dayString => {
      if (prev.includes(dayString)) {
        const index = updated.indexOf(dayString);
        if (index !== -1) {
          updated.splice(index, 1);
        }
      } else {
        updated.push(dayString);
      }
    });

    return set(collapsedDaysAtom, updated);
  },
);

export const useToggleMultipleCollapsedDay = () =>
  useSetAtom(toggleMultipleCollapsedDayAtom);

export const useToggleCollapsedDay = () => useSetAtom(toggleCollapsedDayAtom);

// I struggled to use a `CJEvent | undefined` with the storage (CahierJournal.test.tsx timed out)
// So I had to stringify it before storing and parse it back myself
// Tried before : mocking localStorage = did't work, update jotai = didn't work, use getOnInit = didn't work, init the storage with an object = kind of worked
// We prefer using sessionStorage because it prevents tab conflicts
const storage = createJSONStorage<string | undefined>(() => sessionStorage);
const draftCJSeanceAtom = atomWithStorage('draftCJSeance', undefined, storage);

export const useResetDraftCJSeance = () => useResetAtom(draftCJSeanceAtom);

export const useSetDraftCJSeance = () => useSetAtom(draftCJSeanceAtom);

export const useDraftCJSeance = () => {
  const draftCJSeance = useAtomValue(draftCJSeanceAtom);

  if (!draftCJSeance) {
    return undefined;
  }

  // dates are stored as strings in localStorage so we have to parse it back to Date
  const parsed = JSON.parse(draftCJSeance) as CJEvent;
  return {
    ...parsed,
    createdAt: new Date(parsed.createdAt),
    start: new Date(parsed.start),
    end: new Date(parsed.end),
  } as CJEvent;
};
